import { createReducer } from '@common/redux-utils';
import {
  AuthorizationPromptViewModel,
  AuthorizationPromptAnswerViewModel,
  AuthoVersionViewModel
} from '@models/AuthorizationPrompt';
import {
  AuthorizationDetailFormActionType,
  SetCVIPromptsLoadingAction,
  SetCVIPromptsAction,
  ChangeMultiPromptLineItemAction,
  SetPromptRepsonseAction,
  SetPromptResponsesAction,
  AddMultiLinePromptLineAction,
  SetAuthoriztionFormSubmittingFlagAction,
  SetAuthoVersionsAction
} from './authorization-form.actions';
import * as uuid from 'uuid/v4';

// TODO: GET SWO AUTH VIEW MODEL
export interface AuthorizationFormState {
  readonly prompts: Array<AuthorizationPromptViewModel>;
  readonly responses: Array<AuthorizationPromptAnswerViewModel>;
  readonly isLoading: boolean;
  readonly isSubmitting: boolean;
  readonly versions?: Array<AuthoVersionViewModel>;
}

export const initialAuthorizationFormState: AuthorizationFormState = {
  prompts: [],
  responses: [],
  isLoading: false,
  isSubmitting: false
};

function handleSetCVIPromptsLoading(
  state: AuthorizationFormState,
  action: SetCVIPromptsLoadingAction
): AuthorizationFormState {
  return {
    ...state,
    isLoading: action.loading
  };
}

function handleSetCVIPrompts(state: AuthorizationFormState, action: SetCVIPromptsAction): AuthorizationFormState {
  const list = action.prompts.slice().map(x => {
    let children: AuthorizationPromptViewModel[] = [];
    if (x.childPrompts && x.childPrompts.length) {
      children = x.childPrompts.map(c => {
        return { ...c, uuid: uuid() };
      });
    }
    return { ...x, uuid: uuid(), childPrompts: children };
  }); // assign a uuid to each prompt
  return {
    ...state,
    prompts: list
  };
}

function handleSetAuthoVersions(state: AuthorizationFormState, action: SetAuthoVersionsAction): AuthorizationFormState {
  return {
    ...state,
    versions: action.result
  };
}

function handleAddMultiLinePrompt(
  state: AuthorizationFormState,
  action: AddMultiLinePromptLineAction
): AuthorizationFormState {
  const parentIndex = state.prompts
    .slice()
    .map(x => x.promptId)
    .indexOf(action.parentPrompt.promptId);
  if (parentIndex === -1) {
    return state;
  }
  let currentPrompts = state.prompts.slice();
  let currentParent = { ...state.prompts[parentIndex] };
  if (!action.childrenToAdd || action.childrenToAdd.length === 0) {
    return state;
  }
  let toAdd: Array<AuthorizationPromptViewModel> = [];
  let newParent = {} as AuthorizationPromptViewModel;
  let children = action.childrenToAdd;

  children.forEach(x => {
    if (!toAdd.find(y => y.promptId === x.prompt.promptId)) {
      toAdd.push({
        ...x.prompt,
        parentUUID: action.parentPrompt.uuid,
        uuid: x.uuid || uuid()
      });
    }
  });
  newParent = {
    ...currentParent,
    childPrompts: [...currentParent.childPrompts!].concat(toAdd)
  };
  currentPrompts[parentIndex] = newParent;

  return {
    ...state,
    prompts: currentPrompts
  };
}

function handleChangeMultiLinePrompt(
  state: AuthorizationFormState,
  action: ChangeMultiPromptLineItemAction
): AuthorizationFormState {
  const parentIndex = state.prompts
    .slice()
    .map(x => x.promptId)
    .indexOf(action.parentPrompt.promptId);
  if (parentIndex === -1) {
    return state;
  }
  let currentPrompts = state.prompts.slice();
  let children = action.parentPrompt.childPrompts;
  if (!children) {
    return state;
  }
  let numberToRemove = 0;
  let uniqueList: Array<number> = [];
  if (action.remove && action.parentPrompt.childPrompts) {
    action.parentPrompt.childPrompts.forEach(x => {
      if (uniqueList.indexOf(x.promptId) === -1) {
        uniqueList.push(x.promptId);
      }
    });
    numberToRemove = uniqueList.length; // count the number of unique prompt IDs in the childPrompts array and remove that many
  }
  children = children.slice();
  let toAdd: Array<AuthorizationPromptViewModel> = [];
  let newParent = {} as AuthorizationPromptViewModel;
  if (!action.remove) {
    children.forEach(x => {
      if (!toAdd.find(y => y.promptId === x.promptId)) {
        toAdd.push({
          ...x,
          parentUUID: action.parentPrompt.uuid,
          uuid: action.childUUID || uuid()
        });
      }
    });
    newParent = {
      ...action.parentPrompt,
      childPrompts: [...action.parentPrompt.childPrompts!].concat(toAdd)
    };
    currentPrompts[parentIndex] = newParent;
  } else if (action.remove && action.parentPrompt.childPrompts!.length >= numberToRemove * 2) {
    // only remove if there's more than 1 option group
    var childPromptsArray = action.parentPrompt.childPrompts!;
    const childPrompts = childPromptsArray.splice(0, childPromptsArray.length - numberToRemove); // remove last N things in array
    newParent = {
      ...action.parentPrompt,
      childPrompts: [...childPrompts]
    };
    currentPrompts[parentIndex] = newParent;
  }

  return {
    ...state,
    prompts: currentPrompts
  };
}

export function handleSetPromptResponse(
  state: AuthorizationFormState,
  action: SetPromptRepsonseAction
): AuthorizationFormState {
  const currentResponses = state.responses.slice();
  const existingResponse = currentResponses.find(x => x.promptUUID === action.response.promptUUID);
  if (!existingResponse) {
    return {
      ...state,
      responses: [...currentResponses, action.response]
    };
  }
  const withoutNewResponse = currentResponses.filter(x => x.promptUUID !== action.response.promptUUID);

  return {
    ...state,
    responses: [...withoutNewResponse, action.response]
  };
}

export function handleSetPromptResponses(state: AuthorizationFormState, action: SetPromptResponsesAction) {
  return {
    ...state,
    responses: action.responses
  };
}

export function handleSetFormSubmissionFlag(
  state: AuthorizationFormState,
  action: SetAuthoriztionFormSubmittingFlagAction
): AuthorizationFormState {
  return {
    ...state,
    isSubmitting: action.submitting
  };
}

export const AuthorizationFormReducer = createReducer<AuthorizationFormState, AuthorizationDetailFormActionType>(
  initialAuthorizationFormState,
  {
    [AuthorizationDetailFormActionType.SetCVIPromptsLoading]: handleSetCVIPromptsLoading,
    [AuthorizationDetailFormActionType.ChangeMultiPromptLineItem]: handleChangeMultiLinePrompt,
    [AuthorizationDetailFormActionType.SetPromptResponse]: handleSetPromptResponse,
    [AuthorizationDetailFormActionType.SetCVIPrompts]: handleSetCVIPrompts,
    [AuthorizationDetailFormActionType.SetPromptResponses]: handleSetPromptResponses,
    [AuthorizationDetailFormActionType.AddMultiPromptLineItem]: handleAddMultiLinePrompt,
    [AuthorizationDetailFormActionType.SetFormSubmittingFlag]: handleSetFormSubmissionFlag,
    [AuthorizationDetailFormActionType.SetAuthoVersions]: handleSetAuthoVersions
  }
);



// WEBPACK FOOTER //
// ./src/authorization/authorization-form.reducer.ts